import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Layers, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [annual, setAnnual] = useState(true);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans((r.data.plans || []).filter((p) => !p.hidden)));
  }, []);

  const choose = async (plan) => {
    if (plan.contact_sales) {
      window.location.href = "mailto:sales@buildforged.com?subject=BuildForged%20Enterprise%20inquiry";
      return;
    }
    if (!user) {
      toast.message("Create an account first to subscribe.");
      nav("/register");
      return;
    }
    setBusy(plan.id);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: plan.id,
        origin_url: window.location.origin,
        billing_period: annual ? "year" : "month",
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f2f0eb] text-[#0a2540]">
      <Seo path="/pricing" title="Pricing" description="Simple plans that scale with your studio — from your first freelance site to a full agency operation. Starter $29, Pro $59, Agency $129, or Enterprise. Monthly or annual billing." />
      <header className="backdrop-blur-xl bg-[#f2f0eb]/80 border-b border-[#d3d3d3] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-black text-xl tracking-tight">
            <div className="w-7 h-7 bg-[#ff3b00] flex items-center justify-center text-white"><Layers size={16} /></div>
            BuildForged
          </Link>
          <Link to={user ? "/dashboard" : "/login"} className="label-tech hover:text-[#ff3b00] flex items-center gap-2"><ArrowLeft size={13} /> {user ? "Dashboard" : "Log in"}</Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="label-tech mb-3">/ PRICING</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Plans that scale with your studio.</h1>
          <p className="mt-4 text-[#475569] text-lg">From your first freelance site to a full agency operation.</p>
          {user && <div className="mt-4 label-tech">CURRENT PLAN: <span className="text-[#ff3b00]">{user.plan?.toUpperCase() || "FREE"}</span></div>}

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center border border-[#0a2540] bg-white" data-testid="billing-toggle">
            <button
              data-testid="toggle-monthly"
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 text-sm font-semibold transition-colors ${!annual ? "bg-[#0a2540] text-white" : "text-[#0a2540]"}`}
            >
              Monthly
            </button>
            <button
              data-testid="toggle-annual"
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 text-sm font-semibold transition-colors ${annual ? "bg-[#0a2540] text-white" : "text-[#0a2540]"}`}
            >
              Annual <span className="text-[#ff3b00]">–20%</span>
            </button>
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {plans.map((p) => {
            const featured = p.popular;
            const current = user?.plan === p.id;
            const showPrice = annual ? p.annual_price : p.price;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`bg-white border p-8 flex flex-col h-full ${featured ? "border-[#ff3b00] shadow-hard-signal lg:-translate-y-3" : "border-[#0a2540] shadow-hard"}`}>
                {featured && <div className="label-tech text-[#ff3b00] mb-2">★ MOST POPULAR</div>}
                <div className="font-heading font-bold text-xl">{p.name}</div>
                <div className="text-sm text-[#475569] mt-1 min-h-[40px]">{p.tagline}</div>
                <div className="mt-5 mb-6">
                  {p.contact_sales ? (
                    <span className="font-heading font-black text-3xl">Custom</span>
                  ) : (
                    <>
                      <span className="font-heading font-black text-4xl">${showPrice}</span>
                      <span className="text-[#475569]">/mo</span>
                      {annual && <div className="label-tech text-[#ff3b00] mt-1">billed annually</div>}
                    </>
                  )}
                </div>
                <ul className="space-y-3 flex-1 mb-8">
                  {p.features.map((f, k) => (
                    <li key={k} className="flex items-start gap-2 text-sm"><Check size={16} className="text-[#ff3b00] mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <button
                  data-testid={`choose-plan-${p.id}`}
                  disabled={busy === p.id || current}
                  onClick={() => choose(p)}
                  className={`w-full py-3 font-semibold transition-colors disabled:opacity-60 ${featured ? "bg-[#ff3b00] text-white hover:bg-[#e63500]" : "bg-[#0a2540] text-white hover:bg-[#ff3b00]"}`}
                >
                  {current ? "Current plan" : busy === p.id ? "Redirecting…" : p.contact_sales ? "Contact sales" : `Upgrade to ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-center label-tech opacity-60 mt-10">Secure recurring billing via Stripe. Cancel anytime. Real payment — your card will be charged.</p>
      </main>
    </div>
  );
}
